import React, { useState, useRef } from 'react';
import { DecisionState } from './types';
import LandingScreen from './components/LandingScreen';
import StepSituation from './components/StepSituation';
import StepOptions from './components/StepOptions';
import StepFinalComment from './components/StepFinalComment';
import StepFlip from './components/StepFlip';
import StepResult from './components/StepResult';
import StepHelpedDonate from './components/StepHelpedDonate';
import './App.css';

const initialState: DecisionState = {
  step: 1,
  showLanding: true,
  situation: '',
  options: ['', ''],
  finalComment: '',
  chosenIndex: null,
  aiReflection: null,
  aiStatus: 'idle',
  helpedAnswer: null,
};

function App() {
  const [state, setState] = useState<DecisionState>(initialState);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  const updateState = (updates: Partial<DecisionState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const goToNextStep = () => {
    const nextStep = Math.min(state.step + 1, 6) as DecisionState['step'];
    updateState({ step: nextStep });

    // Scroll to next step
    setTimeout(() => {
      if (stepRefs.current[nextStep - 1]) {
        stepRefs.current[nextStep - 1]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  const startOver = () => {
    setState(initialState);
    setTimeout(() => {
      if (stepRefs.current[0]) {
        stepRefs.current[0]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  const flipAgain = () => {
    // Reset flip-related state and go back to step 4
    updateState({
      chosenIndex: null,
      aiReflection: null,
      aiStatus: 'idle',
      step: 4
    });
    setTimeout(() => {
      if (stepRefs.current[3]) {
        stepRefs.current[3]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  const continueFromLanding = () => {
    updateState({ showLanding: false });
  };

  const renderStep = (stepNumber: number, component: React.ReactNode) => {
    return (
      <div
        key={stepNumber}
        ref={el => stepRefs.current[stepNumber - 1] = el}
        className="step-section"
      >
        {component}
      </div>
    );
  };

  if (state.showLanding) {
    return (
      <div className="app">
        <LandingScreen onContinue={continueFromLanding} />
      </div>
    );
  }

  return (
    <div className="app">
      {state.step >= 1 && renderStep(1, (
        <StepSituation
          state={state}
          onUpdate={updateState}
          onNext={goToNextStep}
          isActive={state.step === 1}
        />
      ))}

      {state.step >= 2 && renderStep(2, (
        <StepOptions
          state={state}
          onUpdate={updateState}
          onNext={goToNextStep}
          isActive={state.step === 2}
        />
      ))}

      {state.step >= 3 && renderStep(3, (
        <StepFinalComment
          state={state}
          onUpdate={updateState}
          onNext={goToNextStep}
          isActive={state.step === 3}
        />
      ))}

      {state.step >= 4 && renderStep(4, (
        <StepFlip
          state={state}
          onUpdate={updateState}
          onNext={goToNextStep}
        />
      ))}

      {state.step >= 5 && renderStep(5, (
        <StepResult
          state={state}
          onUpdate={updateState}
          onNext={goToNextStep}
          onFlipAgain={flipAgain}
          onStartOver={startOver}
        />
      ))}

      {state.step >= 6 && renderStep(6, (
        <StepHelpedDonate
          state={state}
          onUpdate={updateState}
        />
      ))}
    </div>
  );
}

export default App;
